import React, { useEffect, useState, useRef } from 'react';
import { Button, Checkbox, Grid, Image, Header, Popup, Label, Message } from 'semantic-ui-react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormikContext, Field } from 'formik';
import _ from 'lodash';

import processingIcon from '../assets/processSqIcon1.gif';
import ingestBookImg from '../assets/shutterstock_2294396055-480x480.jpg';
import processedDataImg from '../assets/processData.png';
import processedDataImgDone from '../assets/robotHoldsBook.svg';

import { FileDropzoneOverImg, FileDropzonePdf } from '../utils/fileDropZone';
import {
  selectFullBookTreeData,
  fetchparseDataFromS3,
  selectParsedStoryData,
  selectFetchparseDataFromS3Status,
  selectFetchparseDataFromS3Error,
  selectClearStory
} from '../slices/storyEditorSlice';
import ConfirmButton from '../utils/confirmButton';
import '../index.css';

/**
 * StoryImporterToolbar Component
 * Simplified toolbar specifically for StoryImporter
 * - No story dropdown (uses preSelectedBook)
 * - Shows book name and author as read-only
 * - Includes title input, file uploads, and submit
 */
const StoryImporterToolbar = ({
  toolBarCallback,
  disabled,
  values,
  isSubmitting,
  storySelectedObj,
  getFullBook,
  preSelectedBook
}) => {
  const dispatch = useDispatch();
  const { submitForm, setFieldValue } = useFormikContext();
  const { title, thumbnail, contentUrl, enabled } = values || {};
  const userProfile = useSelector(state => state.user.profile);
  const hasFullBook = useSelector(selectFullBookTreeData);
  const clearStory = useSelector(selectClearStory);

  // State for file management
  const [thumbnailFileName, setThumbnailFileName] = useState(null);
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [bookPdfFileUrl, setBookPdfFileUrl] = useState(null);
  const [bookPdfFilename, setBookPdfFileName] = useState(null);
  const [ingestBookImgUrl, setIngestBookImgUrl] = useState(null);
  const [parsedStoryData, setParsedStoryData] = useState([]);

  const [submitPopupOpen, setSubmitPopupOpen] = useState(false);
  const [showProcessing, setShowProcessing] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const parsedStoryDataFetched = useSelector(selectParsedStoryData);
  const fetchparseDataFromS3Status = useSelector(selectFetchparseDataFromS3Status);
  const fetchparseDataFromS3Error = useSelector(selectFetchparseDataFromS3Error);
  const recentMsg = useRef('');

  // SSE message handling
  const recentMessage = useSelector((state) => state.user.sse.recentMessage);
  const messages = useSelector((state) => state.user.sse.messages);

  useEffect(() => {
    if (recentMessage.data !== undefined) {
      console.log('New message received:', recentMessage.data.message, 'chunk', recentMessage.data.index);
      if (recentMessage.data.index === undefined) {
        recentMsg.current = "Processing .pdf file..."
      } else {
        recentMsg.current = 'Processing group ' + recentMessage.data.index;
      }
    } else {
      recentMsg.current = "";
    }
  }, [recentMessage, messages]);

  // Set author field from preSelectedBook when it changes
  useEffect(() => {
    if (preSelectedBook) {
      // Log the entire preSelectedBook object to see what fields are available
      console.log('[StoryImporterToolbar] preSelectedBook object:', preSelectedBook);
      console.log('[StoryImporterToolbar] preSelectedBook keys:', Object.keys(preSelectedBook));

      // Extract author from the authors array
      let author = 'Unknown Author';

      // Check if authors array exists and has at least one author
      if (preSelectedBook.authors && Array.isArray(preSelectedBook.authors) && preSelectedBook.authors.length > 0) {
        // Get the first author's name
        author = preSelectedBook.authors[0].name || 'Unknown Author';
      } else if (preSelectedBook.author) {
        // Fallback to direct author field if it exists
        author = preSelectedBook.author;
      } else if (preSelectedBook.authorName) {
        // Another fallback
        author = preSelectedBook.authorName;
      } else if (preSelectedBook.authorFirstName && preSelectedBook.authorLastName) {
        // Construct from first and last name
        author = `${preSelectedBook.authorFirstName} ${preSelectedBook.authorLastName}`;
      }

      console.log('[StoryImporterToolbar] Extracted author:', author);
      setFieldValue('author', author);
    }
  }, [preSelectedBook, setFieldValue]);

  const handleClear = () => {
    // Clear local state
    setThumbnailUrl(null);
    setBookPdfFileUrl(null);
    setBookPdfFileName(null);
    setIngestBookImgUrl(null);
    setParsedStoryData([]);
    setThumbnailFileName(null);
    setShowProcessing(false);
    setIsProcessing(false);

    // Reset Formik form fields
    setFieldValue('title', '');
    setFieldValue('thumbnail', null);
    setFieldValue('pdfFile', null);
    setFieldValue('contentUrl', './shadowResponse.pdf');
    setFieldValue('storyFileName', '');
    setFieldValue('enabled', true);

    toolBarCallback("./shadowResponse.pdf");
  };

  // Fetch parsed data when PDF is uploaded
  useEffect(() => {
    if (hasFullBook && bookPdfFilename && userProfile?.data?.userId) {
      let jsonFile = bookPdfFilename.replace(/\.pdf$/, '.json');
      console.log('bookPdfFileUrl to json', jsonFile, 'hasFullBook', hasFullBook);
      dispatch(fetchparseDataFromS3({
        userId: userProfile.data.userId,
        targetUrl: `${hasFullBook.bookTree.uuid}/book-process-items/${jsonFile}`
      }));
    }
  }, [dispatch, bookPdfFilename, hasFullBook, userProfile]);

  useEffect(() => {
    if (fetchparseDataFromS3Status === 'succeeded') {
      setParsedStoryData(parsedStoryDataFetched || []);
    } else if (fetchparseDataFromS3Status === 'failed') {
      setParsedStoryData(null);
    }
  }, [fetchparseDataFromS3Status, parsedStoryDataFetched, fetchparseDataFromS3Error]);

  useEffect(() => {
    handleClear();
    console.log('StoryImporterToolbar clearStory');
  }, [clearStory]);

  // Format story data for display
  const formatStoryDataJSON = (storyData) => {
    let dialogueArray = storyData;

    if (storyData && typeof storyData === 'object' && !Array.isArray(storyData)) {
      if (storyData.dnObj && storyData.dnObj.dialogueArray) {
        dialogueArray = storyData.dnObj.dialogueArray;
      }
    }

    if (_.isEmpty(dialogueArray) || !Array.isArray(dialogueArray)) {
      return (<div>No Story Data Object File(json) Found</div>);
    }

    let index = -1;
    return (
      <div style={{ height: "500px", overflowY: "scroll" }}>
        <dl>
          {dialogueArray.map(x => {
            if (x.text && x.text.length > 0) {
              index++;
              return (
                <div key={`storyItem${index.toString()}`}>
                  <dt>{x.key} - {x.emotion}</dt>
                  <dd>{x.text}</dd>
                </div>
              );
            }
            return null;
          })}
        </dl>
      </div>
    );
  };

  // Validation helpers
  const enableSubmit = () => {
    return !_.isEmpty(thumbnail) &&
      (!_.isEmpty(contentUrl) && contentUrl !== "./shadowResponse.pdf") &&
      title !== '' &&
      values.author !== ''; // Ensure author is populated
  };

  const enableClearAll = () => {
    return !_.isEmpty(thumbnail) ||
      !_.isEmpty(contentUrl) ||
      title !== '';
  };

  let displayProcessing = showProcessing && enableSubmit();

  // Helper component to show required fields
  const FilenameHelper = () => {
    return (
      <dl className="custom-toolbar-filename-helper">
        <dt>Book:</dt>
        <dd>{preSelectedBook?.name || <span>*Book required...</span>}</dd>

        <dt>Author:</dt>
        <dd>{values.author || <span>*Author required...</span>}</dd>

        <dt>Title:</dt>
        <dd>{title || <span>*Title required...</span>}</dd>

        <dt>Book cover:</dt>
        <dd>{thumbnailFileName || <span>*Book Cover not selected...</span>}</dd>

        <dt>Story pdf:</dt>
        <dd>{bookPdfFilename || <span>*Book .pdf file not selected...</span>}</dd>
      </dl>
    );
  };

  // Submit button component
  const SubmitHelper = () => {
    return (
      <div className="custom-toolbar-submit-container">
        <Popup
          trigger={
            <Button
              disabled={!enableSubmit() || isSubmitting || isProcessing}
              color='blue'
              style={{ width: "100%" }}
            >
              {isSubmitting ? 'Processing...' : 'Submit'}
            </Button>
          }
          on='click'
          position='top center'
          open={submitPopupOpen}
          onOpen={() => setSubmitPopupOpen(true)}
          onClose={() => setSubmitPopupOpen(false)}
          header={"Confirm Story Import"}
          content={
            <>
              <p>Are you sure you want to submit? This can be an extended time process.</p>
              <p className="custom-toolbar_confirm_hint">Please do not navigate away from this page or close the app while processing...</p>
              <div style={{ marginTop: '10px' }}>
                <Button color='red' onClick={() => setSubmitPopupOpen(false)}>No</Button>
                <Button color='green' onClick={() => {
                  submitForm();
                  setShowProcessing(true);
                  setSubmitPopupOpen(false);
                  setIsProcessing(true);
                }}>Yes</Button>
              </div>
            </>
          }
        />
      </div>
    );
  };

  return (
    <div style={{
      pointerEvents: disabled ? 'none' : 'auto',
      opacity: disabled ? 0.5 : 1,
    }}>

      {/* Processing Status Message */}
      {(isSubmitting || isProcessing) && (
        <div className="custom-toolbar-processing-status">
          <p className="custom-toolbar_confirm_hint">Please do not navigate away from this page or close the app while processing...</p>
          <span style={{ color: "#065c94", fontWeight: 'bold' }}>{recentMsg.current}</span>
        </div>
      )}

      {/* Top Row: Display Book Info + Title Input + Enable */}
      <div className='custom-toolbar_story_selection_container custom-toobar-form-group-border'>
        <div className="custom-toolbar-form-group-gap ">

          {/* Book Name Display (Read-only) */}
          {/* <div className="custom-toolbar-form-group">
            <Label className='custom-toolbar-label form-label'>Book:</Label>
            <div style={{
              padding: '8px 14px',
              background: '#f8f8f8',
              border: '1px solid rgba(34,36,38,.15)',
              borderRadius: '4px',
              minWidth: '200px',
              display: 'inline-block'
            }}>
              {preSelectedBook?.name || 'No book selected'}
            </div>
          </div> */}

          {/* Author Display (Read-only) */}
          {/* <div className="custom-toolbar-form-group">
            <Label className='custom-toolbar-label form-label'>Author:</Label>
            <div style={{
              padding: '8px 14px',
              background: '#f8f8f8',
              border: '1px solid rgba(34,36,38,.15)',
              borderRadius: '4px',
              minWidth: '150px',
              display: 'inline-block'
            }}>
              {values.author || 'Unknown Author'}
            </div>
          </div> */}

          {/* Title Input Field */}
          <div className="custom-toolbar-form-group">
            <Label className='custom-toolbar-label form-label'>Title*:</Label>
            <Field name="title">
              {({ field }) => (
                <input
                  {...field}
                  type="text"
                  placeholder="Enter Story Title"
                  className="custom-toolbar-input-field"
                />
              )}
            </Field>
          </div>

          {/* Enable Checkbox */}
          <div className="custom-toolbar-form-group">
            <Label className='custom-toolbar-label form-label'>Enable:</Label>
            <Field name="enabled">
              {({ field }) => (
                <Checkbox
                  style={{ paddingLeft: "8px" }}
                  checked={field.value}
                  onChange={(e, { checked }) => setFieldValue('enabled', checked)}
                />
              )}
            </Field>
          </div>
        </div>

        {/* Clear button on the right */}
        <div className="custom-toolbar-helper-options">
          {enableClearAll() && (
            <ConfirmButton title="Story Importer" handleClear={handleClear} />
          )}
        </div>
      </div>

      {/* Bottom Row: File Selectors + Processing + Submit */}
      <div className="custom-toolbar-bottom-row">

        {/* Book Cover Selector */}
        <div className="custom-toolbar-file-selector stdBoxShadow">
          <FileDropzoneOverImg
            onDropCallback={(file, url) => {
              setThumbnailUrl(url);
              setThumbnailFileName(file.name);
              setFieldValue('thumbnail', file);
            }}
            hintText={<div><span>Book Cover:</span><br /><span>Drag & drop or click</span></div>}
            altText={"Book Cover preview"}
            imgItem={thumbnailUrl}
          />
          {thumbnailFileName ? (
            <div className="custom-toolbar-filename-display">
              {thumbnailFileName}
            </div>
          ) : (
            <div className="custom-toolbar-filename-display">
              {"Select Book Cover"}
            </div>
          )}
        </div>

        {/* PDF File Selector */}
        <div className="custom-toolbar-file-selector stdBoxShadow">
          <FileDropzonePdf
            onDropCallback={(file, url) => {
              setBookPdfFileUrl(url);
              setBookPdfFileName(file.name);
              setIngestBookImgUrl(ingestBookImg);
              setFieldValue('contentUrl', url);
              setFieldValue('storyFileName', file.name);
              setFieldValue('pdfFile', file);
            }}
            hintText={<div><span>PDF File:</span><br /><span>Drag & drop or click</span></div>}
            altText={"Book PDF file selection"}
            imgItem={ingestBookImgUrl}
            showOverLay={false}
          />
          {bookPdfFilename ? (
            <div className="custom-toolbar-filename-display">
              {bookPdfFilename}
            </div>
          ) : (
            <div className="custom-toolbar-filename-display">
              {"Select Book File"}
            </div>
          )}
        </div>

        {/* Processing Indicator */}
        {(displayProcessing || isSubmitting) && (
          <div className="custom-toolbar-processing-indicator stdBoxShadow">
            <img
              src={processingIcon}
              alt="Processing"
              style={{ width: '100px', height: '87px' }}
            />
            <div className="custom-toolbar-filename-display">
              Processing Story
            </div>
          </div>
        )}

        {/* Processed Data Viewer */}
        {bookPdfFileUrl && !displayProcessing && !isSubmitting && (
          <div className="custom-toolbar-processed-data stdBoxShadow">
            <Popup
              position={"top left"}
              offset={[25, 20]}
              on="click"
              content={
                <div style={{ height: "auto", width: "500px", border: "none", padding: "10px" }}>
                  <div style={{ color: "#000000", marginTop: "-10px" }}>
                    <Header as='h3'>Story Data</Header>
                    <Header as='h5'>File: {bookPdfFilename}</Header>
                    {formatStoryDataJSON(parsedStoryData)}
                  </div>
                </div>
              }
              trigger={
                <Image
                  src={!_.isEmpty(ingestBookImgUrl) ? processedDataImgDone : processedDataImg}
                  className="custom-toolbar-icon-button"
                  style={{ width: '100px' }}
                />
              }
            />
            <div className="custom-toolbar-filename-display">
              Story to Process
            </div>
          </div>
        )}

        {/* Spacer to push Submit button to the right */}
        <div className="custom-toolbar-spacer"></div>

        {/* Submit Button */}
        <Grid style={{ maxWidth: "350px" }}>
          <Grid.Row style={{ paddingBottom: "0px" }}>
            <Grid.Column>
              <SubmitHelper />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row style={{ paddingTop: "0px" }}>
            <Grid.Column>
              <FilenameHelper />
            </Grid.Column>
          </Grid.Row>
        </Grid>

      </div>
    </div>
  );
};

export default StoryImporterToolbar;
